import {
  buildStyles,
  CircularProgressbarWithChildren,
} from "react-circular-progressbar";
import RecursiveProgressbar from "./RecursiveProgressbar";
import { CARD_COLOR, CARD_ROTATION } from "./Agenda";
import { formatTime } from "../../helper/timeCalculations";
import { useEffect, useRef, useState } from "react";
import LiveControls from "./LiveControls";
import MiniTimer from "./MiniTimer";
import usePictureInPicture, {
  pipSupported,
} from "../../helper/usePictureInPicture";
import useWakeLock from "../../helper/useWakeLock";
import useTimerData from "../../helper/useTimerData";
import useTimerSound from "../../helper/useTimerSound";
import { IconButton } from "@mui/material";
import { useLanguage } from "../../i18n/LanguageContext";
import {
  Fullscreen,
  FullscreenExit,
  OpenInFull,
  PictureInPictureAlt,
  VolumeOff,
  VolumeUp,
} from "@mui/icons-material";
import { Link, useLocation } from "react-router-dom";

function useIsFullscreen(elementRef) {
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const handleChange = () =>
      setIsFullscreen(document.fullscreenElement === elementRef.current);
    document.addEventListener("fullscreenchange", handleChange);
    return () => document.removeEventListener("fullscreenchange", handleChange);
  }, [elementRef]);

  return isFullscreen;
}

// Keeps the ring's own footprint (and the "Time left" row below it) present
// in every state - empty, running, finished - so the layout never jumps
// around as cards are added or the session finishes.
export default function Progress({ enabled, warningLeadSeconds, onToggleEnabled }) {
  const widgetRef = useRef(null);
  const isFullscreen = useIsFullscreen(widgetRef);
  const location = useLocation();
  const { language, t } = useLanguage();
  const { pipWindow, open: openMini, close: closeMini } = usePictureInPicture();

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      widgetRef.current?.requestFullscreen();
    }
  };

  const {
    starttime,
    agenda,
    isEmpty,
    isOver,
    value,
    maxValue,
    timeLeft,
    totalDuration,
    miniAgenda,
    activeCard,
    now,
    isPaused,
  } = useTimerData(language);

  useWakeLock(Boolean(activeCard) && !isPaused);
  useTimerSound({
    enabled,
    warningLeadSeconds,
    isEmpty,
    isOver,
    activeCard,
    starttime,
  });

  let center = (
    <div className="ring-center">
      <div className="ring-topic">{t("common.noCardsYet")}</div>
    </div>
  );

  if (starttime && agenda.length > 0) {
    center = isOver ? (
      <div className="ring-center">
        <div className="ring-topic">{t("common.done")}</div>
      </div>
    ) : (
      <div style={{ width: "84%" }}>
        <RecursiveProgressbar
          maxValue={maxValue}
          array={agenda}
          strokeWidth={8}
          starttime={starttime}
          actualTime={now}
        />
      </div>
    );
  }

  return (
    <div
      ref={widgetRef}
      className={`timer-widget${isFullscreen ? " is-fullscreen" : ""}`}
    >
      <div className="timer-fullscreen-btn-dock">
        <IconButton
          className="timer-fullscreen-btn"
          size="small"
          component={Link}
          to={{ pathname: "/fullscreen", search: location.search }}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={t("timer.openFullPage")}
        >
          <OpenInFull fontSize="small" />
        </IconButton>
        {pipSupported && !isFullscreen && (
          <IconButton
            className="timer-fullscreen-btn"
            size="small"
            aria-label={pipWindow ? t("mini.close") : t("mini.open")}
            title={pipWindow ? t("mini.close") : t("mini.open")}
            aria-pressed={Boolean(pipWindow)}
            onClick={pipWindow ? closeMini : openMini}
          >
            <PictureInPictureAlt fontSize="small" />
          </IconButton>
        )}
        <IconButton
          className="timer-fullscreen-btn"
          size="small"
          aria-label={
            isFullscreen ? t("timer.exitFullscreen") : t("timer.enterFullscreen")
          }
          onClick={toggleFullscreen}
        >
          {isFullscreen ? (
            <FullscreenExit fontSize="small" />
          ) : (
            <Fullscreen fontSize="small" />
          )}
        </IconButton>
        <IconButton
          className="timer-fullscreen-btn"
          size="small"
          aria-label={enabled ? t("timer.mute") : t("timer.unmute")}
          onClick={() => onToggleEnabled(!enabled)}
        >
          {enabled ? (
            <VolumeUp fontSize="small" />
          ) : (
            <VolumeOff fontSize="small" />
          )}
        </IconButton>
      </div>
      <div className="row">
        <div className="col-md-12">
          <div style={{ width: "100%", height: "100%" }}>
            <CircularProgressbarWithChildren
              value={value}
              strokeWidth={8}
              maxValue={maxValue}
              styles={buildStyles({
                pathColor: "#9A4B4F",
                strokeLinecap: "butt",
                trailColor: "rgba(46, 42, 34, 0.08)",
              })}
            >
              {center}
            </CircularProgressbarWithChildren>
          </div>
        </div>
        <div className="col-md-12 time-left">
          <span className="cap">{t("common.timeLeft")}</span>
          <span className="digits">{timeLeft}</span>
        </div>
      </div>

      <LiveControls isPaused={isPaused} isRunning={Boolean(activeCard)} />

      {pipWindow && (
        <MiniTimer
          pipWindow={pipWindow}
          starttime={starttime}
          agenda={agenda}
          now={now}
          activeCard={activeCard}
          isEmpty={isEmpty}
          isOver={isOver}
          isPaused={isPaused}
          warningLeadSeconds={warningLeadSeconds}
        />
      )}

      {isFullscreen && starttime && (
        <div className="timer-fullscreen-side">
          <div className="timer-fullscreen-meta">
            <span>{t("common.start", { time: formatTime(starttime) })}</span>
            {totalDuration && (
              <>
                <span className="timer-fullscreen-meta-sep">·</span>
                <span>{t("common.total", { duration: totalDuration })}</span>
              </>
            )}
          </div>

          {miniAgenda.length > 0 && (
            <div className="timer-fullscreen-agenda">
              {miniAgenda.map(({ task, status, start, end }) => (
                <div
                  key={task.id}
                  className="mini-card"
                  data-status={status}
                  data-checked={Boolean(task.done)}
                  style={{ transform: `rotate(${CARD_ROTATION[status]})` }}
                >
                  {task.done ? (
                    <span className="mini-card-check" aria-label={t("common.completed")}>
                      ✓
                    </span>
                  ) : (
                    <span
                      className="mini-card-dot"
                      style={{ background: CARD_COLOR[status] }}
                    />
                  )}
                  <span className="mini-card-topic">
                    {task.topic || t("common.untitled")}
                  </span>
                  <span className="mini-card-time">
                    {formatTime(start)}–{formatTime(end)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
